import React, { useState, useEffect } from "react";
import "./App.css";

// Image assets (local)
import imgLogoGroup from "./assets/logo-group.svg";
import imgLogoVector from "./assets/logo3.png";
import imgArrowVector from "./assets/arrow-vector.svg";
import imgScroll from "./assets/scroll.svg";
import imgCalendarBlank from "./assets/calendar-blank.svg";
import imgMicrosoftExcelLogo from "./assets/microsoft-excel-logo.svg";
import imgNotePencil from "./assets/note-pencil.svg";
import imgShuffleSimple from "./assets/shuffle-simple.svg";
import imgCheck from "./assets/check.svg";
import imgHeroBg from "./assets/hero-bg.png";

function App() {
	const [headerVisible, setHeaderVisible] = useState(true);
	const [lastScrollY, setLastScrollY] = useState(0);

	useEffect(() => {
		const handleScroll = () => {
			const currentScrollY = window.scrollY;

			if (currentScrollY < 100) {
				// Always visible in hero section
				setHeaderVisible(true);
			} else if (currentScrollY < lastScrollY) {
				// Scrolling UP - show navbar
				setHeaderVisible(true);
			} else {
				// Scrolling DOWN - hide navbar
				setHeaderVisible(false);
			}

			setLastScrollY(currentScrollY);
		};

		window.addEventListener("scroll", handleScroll, { passive: true });
		return () => window.removeEventListener("scroll", handleScroll);
	}, [lastScrollY]);

	return (
		<div className="landing-page">
			{/* Background gradient overlay with hero image */}
			<div className="bg-gradient-overlay">
				<img src={imgHeroBg} alt="" className="bg-hero-image" />
			</div>
			{/* Header / Navigation */}
			<header className={`header ${headerVisible ? "header-visible" : ""}`}>
				<nav className="nav-container">
					<div className="logo">
						<div className="logo-icon" style={{ position: "relative" }}>
							{/* <img src={imgLogoGroup} alt="" style={{ position: "absolute", width: "100%", height: "100%" }} /> */}
							<img src={imgLogoVector} alt="" style={{ position: "absolute", width: "180%", height: "150%", top: "-20%", left: "0%" }} />
						</div>
						<span className="logo-text">Kinople</span>
					</div>

					<div className="nav-links">
						<a href="#product" className="nav-link">
							Product
						</a>
						<a href="#how-it-works" className="nav-link">
							How it Works
						</a>
						<a href="#features" className="nav-link">
							Features
						</a>
					</div>

					<div className="nav-buttons">
						<a href="https://kinople.github.io/webapp-frontend/#" className="btn-login">
							Login
						</a>
						<a href="https://kinople.github.io/webapp-frontend/#" className="btn-get-started">
							Get Started
						</a>
					</div>
				</nav>
			</header>
			{/* Hero Section */}
			<section className="hero-section">
				<h1 className="hero-title">
					Pre-Production Companion for
					<br />
					<span className="hero-title-highlight">Film</span> & <span className="hero-title-highlight">OTT</span> Productions
				</h1>
				<p className="hero-subtitle">
					Automate breakdowns, scheduling, call sheets and production reports using AI and save weeks of time spent by direction teams
				</p>
				<div className="hero-buttons">
					<a href="https://kinople.github.io/webapp-frontend/#" className="btn-primary-large">
						Get Started
					</a>
					<a
						href="https://cal.com/sujith-kumar/product-demo-w-kinople"
						target="_blank"
						rel="noopener noreferrer"
						className="btn-secondary-large"
					>
						Book a Demo
						<img src={imgArrowVector} alt="" className="arrow-icon" />
					</a>
				</div>
			</section>
			{/* Features Section */}
			<section className="features-section" id="features">
				<div className="features-badge">Features</div>
				<h2 className="features-title">Built for seamless production management</h2>
				<p className="features-subtitle">Integrate and automate all workflow of creative logistics, end-to-end</p>

				<div className="features-grid">
					<FeatureCard
						icon={imgScroll}
						title="Instant Script Breakdown"
						description="Upload script to automatically generate scene breakdowns with 15+ elements"
					/>
					<FeatureCard
						icon={imgCalendarBlank}
						title="Smart Scheduling"
						description="Select available dates of actors and locations to generate optimized schedule"
					/>
					<FeatureCard
						icon={imgMicrosoftExcelLogo}
						title="Integrated Call Sheets"
						description="Get auto-populated call sheets based on breakdown and schedules prepared"
					/>
					<FeatureCard
						icon={imgNotePencil}
						title="Daily Progress Report"
						description="Prepare and analyze your DPRs of previous shoot days to automatically update schedule"
					/>
				</div>
			</section>
			{/* Central Hub Section */}
			<section className="central-hub-section" id="how-it-works">
				{/* Red glow - top left */}
				<div className="hub-glow hub-glow-tl"></div>

				{/* Red glow - bottom right */}
				<div className="hub-glow hub-glow-br"></div>

				{/* Cards Grid */}
				<div className="hub-cards-wrapper">
					{/* Row 1 */}
					<div className="hub-card hub-card-hidden hub-card-tl"></div>
					<div className="hub-card hub-card-hidden hub-card-tc"></div>
					<div className="hub-card hub-card-hidden hub-card-tr"></div>

					{/* Row 2 */}
					<div className="hub-card hub-card-hidden hub-card-ml"></div>
					<div className="hub-card hub-card-center"></div>
					<div className="hub-card hub-card-hidden hub-card-mr"></div>

					{/* Row 3 */}
					<div className="hub-card hub-card-hidden hub-card-bl"></div>
					<div className="hub-card hub-card-hidden hub-card-bc"></div>
					<div className="hub-card hub-card-hidden hub-card-br"></div>
				</div>

				{/* Corner Connectors */}
				<svg className="hub-connector hub-connector-tl" xmlns="http://www.w3.org/2000/svg" width="78" height="74" viewBox="0 0 78 74" fill="none">
					<path
						d="M4.28572 74C3.14907 74 2.05899 73.5127 1.25526 72.6454C0.45153 71.778 0 70.6016 0 69.375L0 13.875C0 6.22522 5.76858 -3.8147e-05 12.8572 -3.8147e-05H72.8572C73.9938 -3.8147e-05 75.0839 0.487221 75.8876 1.35458C76.6914 2.22194 77.1429 3.39834 77.1429 4.62497C77.1429 5.85159 76.6914 7.028 75.8876 7.89536C75.0839 8.76271 73.9938 9.24997 72.8572 9.24997H12.8572C11.7205 9.24997 10.6304 9.73723 9.82669 10.6046C9.02296 11.4719 8.57143 12.6483 8.57143 13.875V69.375C8.57143 70.6016 8.1199 71.778 7.31618 72.6454C6.51245 73.5127 5.42236 74 4.28572 74Z"
						fill="#CFCDCD"
						fill-opacity="0.8"
					/>
				</svg>
				<svg className="hub-connector hub-connector-tr" xmlns="http://www.w3.org/2000/svg" width="78" height="74" viewBox="0 0 78 74" fill="none">
					<path
						d="M72.8572 74C73.9939 74 75.084 73.5127 75.8877 72.6454C76.6914 71.778 77.1429 70.6016 77.1429 69.375V13.875C77.1429 6.22522 71.3744 -3.8147e-05 64.2858 -3.8147e-05H4.28576C3.14912 -3.8147e-05 2.05903 0.487221 1.2553 1.35458C0.451576 2.22194 3.8147e-05 3.39834 3.8147e-05 4.62497C3.8147e-05 5.85159 0.451576 7.028 1.2553 7.89536C2.05903 8.76271 3.14912 9.24997 4.28576 9.24997H64.2858C65.4224 9.24997 66.5125 9.73723 67.3163 10.6046C68.12 11.4719 68.5715 12.6483 68.5715 13.875V69.375C68.5715 70.6016 69.023 71.778 69.8268 72.6454C70.6305 73.5127 71.7206 74 72.8572 74Z"
						fill="#CFCDCD"
						fill-opacity="0.8"
					/>
				</svg>

				<svg className="hub-connector hub-connector-bl" xmlns="http://www.w3.org/2000/svg" width="78" height="74" viewBox="0 0 78 74" fill="none">
					<path
						d="M4.28572 0C3.14907 0 2.05899 0.487274 1.25526 1.35463C0.45153 2.22199 0 3.39838 0 4.625L0 60.125C0 67.7748 5.76858 74 12.8572 74H72.8572C73.9938 74 75.0839 73.5128 75.8876 72.6454C76.6914 71.7781 77.1429 70.6017 77.1429 69.375C77.1429 68.1484 76.6914 66.972 75.8876 66.1046C75.0839 65.2373 73.9938 64.75 72.8572 64.75H12.8572C11.7205 64.75 10.6304 64.2628 9.82669 63.3954C9.02296 62.5281 8.57143 61.3517 8.57143 60.125V4.625C8.57143 3.39838 8.1199 2.22199 7.31618 1.35463C6.51245 0.487274 5.42236 0 4.28572 0Z"
						fill="white"
						fill-opacity="0.6"
					/>
				</svg>

				<svg className="hub-connector hub-connector-br" xmlns="http://www.w3.org/2000/svg" width="78" height="74" viewBox="0 0 78 74" fill="none">
					<path
						d="M72.8572 0C73.9939 0 75.084 0.487274 75.8877 1.35463C76.6914 2.22199 77.1429 3.39838 77.1429 4.625V60.125C77.1429 67.7748 71.3744 74 64.2858 74H4.28576C3.14912 74 2.05903 73.5128 1.2553 72.6454C0.451576 71.7781 3.8147e-05 70.6017 3.8147e-05 69.375C3.8147e-05 68.1484 0.451576 66.972 1.2553 66.1046C2.05903 65.2373 3.14912 64.75 4.28576 64.75H64.2858C65.4224 64.75 66.5125 64.2628 67.3163 63.3954C68.12 62.5281 68.5715 61.3517 68.5715 60.125V4.625C68.5715 3.39838 69.023 2.22199 69.8268 1.35463C70.6305 0.487274 71.7206 0 72.8572 0Z"
						fill="#CFCDCD"
						fill-opacity="0.8"
					/>
				</svg>

				{/* Center Content */}
				<div className="hub-content">
					<h2 className="hub-title">Central Hub for all production paperwork</h2>
					<p className="hub-description">
						Maintain all production-specific paperwork in one place. Bring your direction team into one shared workspace for smooth
						collaboration
					</p>
				</div>
			</section>
			{/* Coverage Section */}
			<section className="coverage-section" id="product">
				<h2 className="coverage-title">
					From script breakdown of first draft till daily production report of last shoot day, we've got you covered
				</h2>
				<div className="coverage-buttons">
					<a href="https://kinople.github.io/webapp-frontend/#" className="btn-primary-large">
						Get Started
					</a>
					<a
						href="https://cal.com/sujith-kumar/product-demo-w-kinople"
						target="_blank"
						rel="noopener noreferrer"
						className="btn-secondary-large"
					>
						Book a Demo
						<img src={imgArrowVector} alt="" className="arrow-icon" />
					</a>
				</div>
			</section>
			{/* Comparison Section */}
			<section className="comparison-section">
				<div className="comparison-badge">Comparison</div>
				<h2 className="comparison-title">
					But, why would you <br></br> want to use <span className="comparison-title-highlight">Kinople?</span>
				</h2>

				<div className="other-products-label">Other Products</div>

				<div className="comparison-cards-container">
					{/* Cons Card - Other Products */}
					<div className="cons-card">
						<div className="cons-list">
							<ConsItem text="Scattered spreadsheets cause constant confusion" />
							<ConsItem text="Info lives across WhatsApp, email, PDFs, sheets" />
							<ConsItem text="Breakdown takes days, sometimes weeks" />
							<ConsItem text="Schedules are manually arranged by date availability, often inefficient" />
							<ConsItem text="Schedule conflict without warning" />
							<ConsItem text="Call sheets need tedious manual updates" />
							<ConsItem text="last-minute changes break every document" />
							<ConsItem text="Miscommunication slows the entire crew" />
						</div>
					</div>

					{/* Kinople Logo */}
					<div className="kinople-logo-large">
						<div className="logo-icon" style={{ position: "relative", width: "57px", height: "57px" }}>
							{/* <img src={imgLogoGroup} alt="" style={{ position: "absolute", width: "100%", height: "100%" }} /> */}
							<img src={imgLogoVector} alt="" style={{  position: "absolute", width: "180%", height: "150%", top: "-20%", left: "0%" }} />
						</div>
						<span className="logo-text">Kinople</span>
					</div>

					{/* Pros Card - Kinople */}
					<div className="pros-card">
						<div className="pros-content">
							<div className="pros-list">
								<ProsItem text="All production data stays perfectly synced" />
								<ProsItem text="One platform replaces scattered tools" />
								<ProsItem text="Breakdown completes in minutes" />
								<ProsItem text="Input all dates and get an optimized schedule using elements efficiently" />
								<ProsItem text="Schedule instantly adjusts to changes" />
								<ProsItem text="Call sheets update automatically" />
								<ProsItem text="Everyone works from the latest version, always" />
								<ProsItem text="Workflows run smoothly end-to-end" />
							</div>
						</div>
					</div>
				</div>
			</section>
			<section className="luminous-arc-bg">
				<div className="arc-glow glow-left"></div>
				<div className="arc arc-left"></div>

				<div className="arc-glow glow-right"></div>
				<div className="arc arc-right"></div>

				<div className="cta-content">
					<h2 className="cta-title">Still sticking to the industry norm or ready to break it?</h2>
					<div className="cta-buttons">
						<a href="https://kinople.github.io/webapp-frontend/#" className="btn-primary-large">
							Get Started
						</a>
						<a
							href="https://cal.com/sujith-kumar/product-demo-w-kinople"
							target="_blank"
							rel="noopener noreferrer"
							className="btn-secondary-large"
						>
							Book a Demo
							<img src={imgArrowVector} alt="" className="arrow-icon" />
						</a>
					</div>
				</div>
			</section>
			{/* Footer */}
			<footer className="footer">
				<div className="footer-content">
					<div className="footer-logo">
						<div className="logo-icon" style={{ position: "relative", width: "57px", height: "57px" }}>
							{/* <img src={imgLogoGroup} alt="" style={{ position: "absolute", width: "100%", height: "100%" }} /> */}
							<img src={imgLogoVector} alt="" style={{  position: "absolute", width: "180%", height: "150%", top: "-20%", left: "0%"  }} />
						</div>
						<span className="logo-text">Kinople</span>
					</div>
					<p className="footer-tagline">Pre-Production Companion for Film and OTT production</p>
				</div>
			</footer>
		</div>
	);
}

// Feature Card Component
function FeatureCard({ icon, title, description }) {
	return (
		<div className="feature-card">
			<div className="feature-content">
				<div className="feature-header">
					<div className="feature-icon-wrapper">
						<div className="feature-icon-bg"></div>
						<img src={icon} alt="" className="feature-icon" />
					</div>
					<h3 className="feature-name">{title}</h3>
				</div>
				<div className="feature-divider"></div>
				<p className="feature-description">{description}</p>
				<div className="feature-divider"></div>
			</div>
		</div>
	);
}

// Cons Item Component
function ConsItem({ text }) {
	return (
		<div className="cons-item">
			<img src={imgShuffleSimple} alt="" className="shuffle-icon" />
			<span className="cons-text">{text}</span>
		</div>
	);
}

// Pros Item Component
function ProsItem({ text }) {
	return (
		<div className="pros-item">
			<img src={imgCheck} alt="" className="check-icon" />
			<span className="pros-text">{text}</span>
		</div>
	);
}

export default App;
